/**
 * Shared helpers for rendering BCP 47 language codes as human-readable
 * captions and canonicalizing their casing. Extracted in PLAT-10066 so
 * both the translation generate modal and the composition quick-edit
 * popover header suffix can share one source of truth.
 */

/**
 * Normalize a BCP 47 code so the language subtag is lowercase and the
 * region subtag is uppercase (e.g. `EN-us` → `en-US`). Leaves any
 * extension subtags (script, variants, etc.) untouched.
 */
export function canonicalizeBcp47(code: string): string {
  const parts = code.split('-');
  if (parts.length === 0) return code;
  const [lang, region, ...rest] = parts;
  const out = [lang.toLowerCase()];
  if (region) out.push(region.toUpperCase());
  for (const tag of rest) out.push(tag);
  return out.join('-');
}

/**
 * Render a BCP 47 code as "Language (Region)" via `Intl.DisplayNames`.
 * Falls back to the canonicalized code when the runtime can't resolve a
 * name (ancient browsers, invalid codes).
 */
export function formatLanguageCaption(code: string): string {
  const canonical = canonicalizeBcp47(code);
  try {
    const names = new Intl.DisplayNames(['en'], {
      type: 'language',
      fallback: 'code',
    });
    const resolved = names.of(canonical);
    return resolved && resolved !== canonical ? resolved : canonical;
  } catch {
    return canonical;
  }
}
